import type { QueryClient } from "@tanstack/react-query";
import { Permission, hasPermission, samePermissions, type Category, type Channel, type CommunityStructure, type PermissionTemplate } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { confirm } from "../../components/ui/Dialog";
import { toast } from "../../components/ui/Toast";

/*
 * Category ↔ channel permission sync on the client: what a category's
 * template is, whether a channel matches it, and the "Sync permissions?"
 * question asked after a channel moves into a different category.
 */

export const categoryTemplate = (c: Category): PermissionTemplate => ({
  isPrivate: c.isPrivate,
  allowedRoleIds: c.allowedRoleIds,
  overwrites: c.overwrites ?? [],
  memberOverwrites: (c.memberOverwrites ?? []).map(({ userId, allow, deny }) => ({ userId, allow, deny })),
});

export const channelTemplate = (c: Pick<Channel, "isPrivate" | "allowedRoleIds" | "overwrites" | "memberOverwrites">): PermissionTemplate => ({
  isPrivate: c.isPrivate,
  allowedRoleIds: c.allowedRoleIds,
  overwrites: c.overwrites ?? [],
  memberOverwrites: (c.memberOverwrites ?? []).map(({ userId, allow, deny }) => ({ userId, allow, deny })),
});

/** Whether the viewer may copy this category's permissions onto a channel (the server's rule). */
export function canSync(structure: CommunityStructure, category: Category) {
  const { community } = structure;
  const perms = community.myPermissions;
  if (community.isOwner || hasPermission(perms, Permission.ADMINISTRATOR)) return true;
  if (!hasPermission(perms, Permission.MANAGE_CHANNELS)) return false;
  const t = categoryTemplate(category);
  return (!t.overwrites.length && !t.memberOverwrites.length) || hasPermission(perms, Permission.MANAGE_ROLES);
}

export async function syncChannel(qc: QueryClient, communityId: string, channelId: string) {
  try {
    await api.patch(`/api/channels/${channelId}`, { syncPermissions: true });
    await qc.invalidateQueries({ queryKey: ["structure", communityId] });
    toast.show("Permissions synced");
  } catch (e) {
    toast.error(errorMessage(e, "Couldn't sync permissions"));
  }
}

/**
 * After a channel lands in a different category: if its permissions differ
 * from the category's, ask whether to sync them or keep the channel's own.
 * Moving into "no category", or into a category with the same permissions,
 * asks nothing.
 */
export async function offerPermissionSync(qc: QueryClient, structure: CommunityStructure, channel: Channel, toCategoryId: string | null) {
  if (!toCategoryId) return;
  const category = structure.categories.find((c) => c.id === toCategoryId);
  // Only managers get the templates; without them there's nothing to compare.
  if (!category || category.overwrites === undefined || channel.overwrites === undefined) return;
  if (samePermissions(channelTemplate(channel), categoryTemplate(category)) || !canSync(structure, category)) return;
  const label = `${channel.type === "voice" || channel.type === "stage" ? "" : "#"}${channel.name}`;
  const ok = await confirm({
    title: "Sync permissions?",
    description: `${label} moved to ${category.name}, but its permissions are different from the category's. Sync them with ${category.name}, or keep the channel's current permissions?`,
    confirmLabel: "Sync permissions",
    cancelLabel: "Keep current",
  });
  if (ok) await syncChannel(qc, structure.community.id, channel.id);
}
